/**
 * Supabase session manager for SELECT + Realtime.
 *
 * Writes go through /api/* (service-role); this JWT only powers RLS-gated
 * reads and Realtime subscriptions. The Supabase client pulls its token
 * through getFreshSupabaseJwt() on EVERY request (registered as the
 * accessToken provider below), which silently re-mints via
 * /api/auth/session when the cached token is missing, expiring, or signed
 * with a dead algorithm — so a tab left open past the 24h token expiry
 * heals itself instead of 401ing every read.
 *
 * HS256 tokens are treated as invalid: Supabase only trusts our ES256
 * signing key since the asymmetric-key migration, and a cached HS256 token
 * (e.g. minted by a stale API deployment) would otherwise poison all reads
 * until it expires.
 */
import { supabase, setSupabaseAccessTokenProvider } from '../lib/supabase';
import { SUPABASE_JWT_KEY, getStoredSupabaseJwt } from '../lib/supabase-token';
import { getSessionId } from './api-client';

interface JwtClaims {
  sui_address?: string;
  exp?: number;
}

function decodeJwtPart(part: string): Record<string, unknown> | null {
  try {
    return JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

function decodeJwt(jwt: string): { header: Record<string, unknown>; claims: JwtClaims } | null {
  const parts = jwt.split('.');
  if (parts.length !== 3) return null;
  const header = decodeJwtPart(parts[0]);
  const claims = decodeJwtPart(parts[1]) as JwtClaims | null;
  if (!header || !claims) return null;
  return { header, claims };
}

// Refuse tokens that expire within the next 60s so an in-flight request
// doesn't race the expiry.
const EXPIRY_SKEW_MS = 60_000;

function isJwtUsable(jwt: string): boolean {
  const decoded = decodeJwt(jwt);
  if (!decoded) return false;
  if (decoded.header.alg !== 'ES256') return false;
  if (decoded.claims.exp && decoded.claims.exp * 1000 < Date.now() + EXPIRY_SKEW_MS) return false;
  return true;
}

export function isSupabaseJwtValidFor(jwt: string, expectedSuiAddress: string): boolean {
  if (!isJwtUsable(jwt)) return false;
  const decoded = decodeJwt(jwt);
  return decoded?.claims.sui_address === expectedSuiAddress;
}

export async function setSupabaseSession(supabaseJwt: string): Promise<void> {
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(SUPABASE_JWT_KEY, supabaseJwt);
    } catch {
      /* storage blocked — realtime auth below still applies for this tab */
    }
  }
  await supabase.realtime.setAuth(supabaseJwt);
}

async function mintSupabaseJwt(): Promise<string | null> {
  const sessionId = getSessionId();
  if (!sessionId) return null;
  try {
    const res = await fetch('/api/auth/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionId}` },
      body: JSON.stringify({ action: 'supabase-jwt' }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.supabaseJwt) return null;
    return data.supabaseJwt;
  } catch {
    return null;
  }
}

// Dedupe concurrent re-mints: a dashboard mount fires many reads at once and
// they must not each hit /api/auth/session.
let mintInFlight: Promise<string | null> | null = null;

/**
 * The Supabase client's accessToken provider. Returns the cached token when
 * usable; otherwise re-mints one from the server session, stores it, and
 * updates Realtime auth. Returns null when there is no session (logged out)
 * or the mint fails — reads then run unauthenticated and RLS returns naught,
 * which is the correct logged-out behavior.
 */
export async function getFreshSupabaseJwt(): Promise<string | null> {
  const stored = getStoredSupabaseJwt();
  if (stored && isJwtUsable(stored)) return stored;
  if (!mintInFlight) {
    mintInFlight = (async () => {
      const fresh = await mintSupabaseJwt();
      if (fresh && isJwtUsable(fresh)) {
        await setSupabaseSession(fresh);
        return fresh;
      }
      return null;
    })().finally(() => {
      mintInFlight = null;
    });
  }
  return mintInFlight;
}

setSupabaseAccessTokenProvider(getFreshSupabaseJwt);

/**
 * Restore the Supabase JWT on app boot. If the cached token is missing,
 * expired, or for a different account, silently mint a new one via the
 * sessions API.
 */
export async function restoreSupabaseSession(userAddress: string | null): Promise<boolean> {
  if (!userAddress) return false;
  if (typeof localStorage === 'undefined') return false;
  const stored = getStoredSupabaseJwt();
  if (stored && isSupabaseJwtValidFor(stored, userAddress)) {
    await setSupabaseSession(stored);
    return true;
  }
  if (stored) {
    try {
      localStorage.removeItem(SUPABASE_JWT_KEY);
    } catch {
      /* ignore */
    }
  }
  const fresh = await getFreshSupabaseJwt();
  return Boolean(fresh && isSupabaseJwtValidFor(fresh, userAddress));
}

export async function clearSupabaseSession(): Promise<void> {
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(SUPABASE_JWT_KEY);
    } catch {
      /* ignore */
    }
  }
  await supabase.realtime.setAuth(null);
}
